import { useEffect, useState } from "react";
import { useInvoicePoller } from "../hooks/useInvoicePoller";
import { StatusMessage } from "./StatusMessage";
import { emitToSdk } from "../messaging";
import { requestInvoice } from "../api";

interface InvoiceConfirmProps {
  clientId: string | null;
}

// Shown when the backend returns 429 (more than 5 invoice requests/hour/merchant).
const RATE_LIMIT_MESSAGE =
  "You have made the maximum number of Stealth requests from this merchant at this time. Please try again later…";

export function InvoiceConfirm({ clientId }: InvoiceConfirmProps) {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const { status, start } = useInvoicePoller();

  const sendInvoice = async () => {
    setSending(true);
    setSendError(null);
    try {
      const { invoiceId } = await requestInvoice(clientId);
      setSent(true);
      emitToSdk("INVOICE_SENT", { clientId: clientId ?? undefined });
      start(invoiceId);
    } catch (e) {
      if ((e as { status?: number }).status === 429) {
        // 429 is retryable — do NOT signal a hard failure the merchant might act
        // on by cancelling the order. Just show the rate-limit state.
        setRateLimited(true);
        setSendError(RATE_LIMIT_MESSAGE);
      } else {
        setSendError(
          e instanceof Error ? e.message : "Could not send invoice.",
        );
        emitToSdk("INVOICE_FAILED", { clientId: clientId ?? undefined });
      }
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (status === "confirmed") {
      emitToSdk("PAYMENT_CONFIRMED", { clientId: clientId ?? undefined });
    } else if (status === "failed") {
      emitToSdk("PAYMENT_FAILED", { clientId: clientId ?? undefined });
    }
  }, [status, clientId]);

  const busy = sending || status === "pending";

  return (
    <div className="nl-invoice">
      <p className="nl-invoice__text">
        We’ll text a secure payment link to your registered phone.
      </p>

      <button
        className="nl-button"
        type="button"
        onClick={sendInvoice}
        disabled={busy || rateLimited}
      >
        {rateLimited
          ? "Try again later"
          : sending
            ? "Sending…"
            : sent
              ? "Resend invoice to phone"
              : "Send invoice to phone"}
      </button>

      {sendError && <StatusMessage variant="error" message={sendError} />}
      {status === "pending" && (
        <StatusMessage
          variant="pending"
          message="Waiting for payment confirmation…"
        />
      )}
      {status === "confirmed" && (
        <StatusMessage
          variant="success"
          message="Payment confirmed. You’re all set."
        />
      )}
      {status === "failed" && (
        <StatusMessage
          variant="resend"
          message="Payment didn’t go through. Resend the invoice to try again."
        />
      )}
    </div>
  );
}
